/* ---------------------------------------------------------------------------
   STL 2026 — PLP facets: collapsed groups and deferred apply

   Serena, 7 Sep 2026. Three behaviours Madrid has no setting for.

   1. COLLAPSED BY DEFAULT. `facets.liquid` renders every group as a <details>
      with `open`. There is no setting and no CSS that can close them — `open` is
      an attribute — so it is removed here, once per element.

   2. DEFERRED APPLY, after minkpink.com. Madrid submits on EVERY change:
      `facets.js` line 12 binds `facetForm.addEventListener("input",
      this.debouncedOnSubmit)`. So ticking three colours fired three fetches and
      re-rendered the grid under the open drawer each time.

      This blocks that listener with a CAPTURE-phase handler on the form. Capture
      runs root -> target, so a capture listener on the FORM fires before the
      event reaches the input and before the form's own bubble listener;
      `stopPropagation()` there means Madrid never sees it. The checkbox still
      ticks — that is the browser's default action, not a listener.

      Apply then lets exactly one event through and re-dispatches it, so the
      submit still runs through Madrid's own `onSubmitHandler`. Nothing about
      the fetch, the URL or the re-render is reimplemented here.

   3. THE AVAILABILITY GROUP IS HIDDEN (the CSS does that), and in-stock RIDES
      ALONG WITH A SIZE SELECTION — Serena, 10 Sep 2026, after finding Lennox
      High Waist Jeans listed under a size L filter with L sold out.

      **THE SHOPIFY FLOW CANNOT COVER THIS CASE, which is why the 7 Sep removal
      left a real gap.** The Flow archives a product whose TOTAL inventory hits
      zero; Lennox has one XXL in stock, so it is correctly still on the store
      and correctly still in the catalogue — it is only wrong under a filter for
      a size it does not have. Whole-product archiving and per-size filtering
      are different problems.

      **Shopify intersects `filter.v.*` at the VARIANT level, not the product
      level** — measured, not assumed. A product matches only if ONE variant
      satisfies every variant filter at once, so `size=L` + `availability=1`
      keeps a product only when it has an available L. Verified on
      /collections/sale: `size=L` returns Lennox, `size=L&availability=1` drops
      it, and `size=XXL&availability=1` keeps it — XXL being its one live size.
      That is exactly the wanted behaviour and needs no per-variant logic here.

      **Scoped to SIZE, and to APPLY.** It is not applied on arrival, which is
      what made the 7 Sep version cost an extra fetch and flash the unfiltered
      grid — the two reasons it was removed. Browsing a collection, or filtering
      by colour or price alone, is unchanged. Sorting after a size filter keeps
      it, because the checkbox is then in the URL and the re-rendered form
      carries it.
   ------------------------------------------------------------------------- */

(function () {
  'use strict';

  var passthrough = false;

  /* ---- 1. collapsed groups ---------------------------------------------- */

  function collapse(root) {
    Array.prototype.forEach.call(
      (root || document).querySelectorAll('.facets__container .facets__disclosure[open]'),
      function (d) {
        /* Once per element. Re-closing on every mutation would fight the
           visitor: Madrid re-renders after each apply, and the group they had
           just been using would snap shut under them. */
        if (d.dataset.stlCollapsed) return;
        d.dataset.stlCollapsed = '1';
        d.removeAttribute('open');
        var summary = d.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      }
    );
  }

  /* ---- 2. hold the submit until Apply ----------------------------------- */

  function guard(container) {
    if (!container || container.dataset.stlHeld) return;
    container.dataset.stlHeld = '1';

    /* BUBBLE PHASE, ON THE DRAWER — not capture on the form.

       The first version captured on the form and stopped every `input` there.
       That does hold the submit, but capture runs root -> target, so stopping
       there means the event never reaches the CONTROL either — and Madrid binds
       real behaviour directly on the controls:

         - `PriceRange` (facets.js 527) binds `change`/`input` on its own two
           <input>s to move the handles, clamp the pair against each other and
           rewrite `--range-min` / `--range-max`. Killed in capture, the slider
           looked dead: the thumbs did not move and the From/To fields never
           filled in. Reported 9 Sep 2026 as "price slider appears not to be
           working" — the paint was one half of it, this was the other.
         - the sort <select>, which is in the same form, was silenced outright
           (fixed 9 Sep by scoping to the drawer; this replaces that scope check
           with the listener's own position).

       Listening on `.facets__container` in the BUBBLE phase puts the stop after
       the target and everything inside the drawer, and before the form — which
       is exactly the boundary "hold the submit until Apply" describes. */
    container.addEventListener('input', function (e) {
      if (passthrough) return;
      e.stopPropagation();
    });
  }

  /* Runs the submit through Madrid's own handler by letting one event past the
     guard. `bubbles` matters: the listener is on the form, not the control. */
  function submit(form) {
    passthrough = true;
    try {
      form.dispatchEvent(new Event('input', { bubbles: true }));
    } finally {
      passthrough = false;
    }
  }

  /* ---- visible feedback while a sort or filter is in flight -------------

     Madrid adds `.loading` to `.collection` at the top of `renderPage`, but that
     element is REPLACED wholesale when the response lands, and instrumenting it
     showed the class arriving on the new grid rather than sitting on the old one
     for the duration — so it cannot be relied on to dim anything.

     This puts the class on `#ProductGridContainer`, which survives the swap, and
     clears it on the first mutation inside it — i.e. the moment the new grid is
     written. Serena, 9 Sep 2026: camillaandmarc.com's sort "loads quicker", and
     measured click-to-rendered it is not (ours 1167ms, theirs 2249ms) — what
     they have and we did not is any sign at all that something is happening.

     The timeout is a safety net: if a request fails there is no mutation, and a
     permanently dimmed grid would be far worse than no feedback. */
  function busy() {
    var gc = document.getElementById('ProductGridContainer');
    if (!gc || gc.classList.contains('stl-busy')) return;
    gc.classList.add('stl-busy');

    var done = function () {
      gc.classList.remove('stl-busy');
      if (obs) obs.disconnect();
      clearTimeout(t);
    };
    /* **NOT `subtree: true`.** Swiper, the card sliders and the badge script all
       mutate deep inside the grid constantly, so a subtree observer fires within
       microseconds of the class going on and the dim is gone before a frame is
       painted — measured: zero frames visible. Madrid replaces the grid with
       `ProductGridContainer.innerHTML = …`, which is a childList mutation on this
       element ITSELF, so watching only its direct children is both sufficient and
       immune to everything else on the page. */
    /* **NOT `subtree: true`, and only a mutation that ADDS nodes.** Two separate
       things were clearing this within microseconds:

         - a subtree observer caught Swiper, the card sliders and the badge script
           mutating deep inside the grid constantly;
         - `onSubmitHandler` itself opens with
           `document.querySelectorAll('.no-js-list').forEach(el => el.remove())`,
           which is a childList mutation on this very element with NO added nodes.

       Madrid rewrites the grid with `ProductGridContainer.innerHTML = …`, which
       always adds nodes — so that is the signal, and it is immune to both. */
    var obs = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes && muts[i].addedNodes.length) { done(); return; }
      }
    });
    obs.observe(gc, { childList: true });
    var t = setTimeout(done, 8000);
  }

  /* `stl-sort-menu.js` submits its own choice, so it needs this too. */
  window.stlFacetsBusy = busy;

  /* ---- Clear all, while nothing has been APPLIED yet -------------------- */

  /* Madrid's Clear all is an <a> to the bare collection URL, and it marks itself
     `.facet-remove--disabled` — `opacity: 0.3` AND `pointer-events: none` —
     whenever the URL carries no filters. Under deferred apply the URL carries
     none until Apply is pressed, so the control was switched off in exactly the
     state a visitor most wants it: boxes ticked, nothing applied, "clear all
     doesn't work" (Serena, 9 Sep 2026).

     The CSS hands the pointer back; this gives the click something to do. */
  function clearPending(root) {
    var cleared = 0;

    Array.prototype.forEach.call(
      root.querySelectorAll('input[type="checkbox"]:checked'),
      function (input) { input.checked = false; cleared++; }
    );

    /* The price group is a slider plus two number fields, all driven by Madrid's
       `PriceRange`, which binds `change` on its own inputs — so resetting the
       values and firing `change` is what repaints the track and the handles.
       That event is safe: the form listens for `input`, not `change`, and the
       drawer guard would hold an `input` anyway. */
    Array.prototype.forEach.call(
      root.querySelectorAll('.facets__price input'),
      function (input) {
        var reset = input.type === 'range'
          ? (input.name && /gte|min/i.test(input.name) ? input.min : input.max)
          : '';
        if (input.value !== reset) { input.value = reset; cleared++; }
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    );

    return cleared;
  }

  function wireClearAll(container) {
    var link = container.querySelector('.active-facets__button-remove');
    if (!link || link.dataset.stlClear) return;
    link.dataset.stlClear = '1';

    link.addEventListener('click', function (e) {
      clearPending(container);

      /* If filters ARE in the URL, let Madrid's own navigation run — that clears
         the URL and the boxes together. If they are not, the href is the page we
         are already on, so following it would reload for nothing and shut the
         drawer; unticking is the whole job. */
      if (!/[?&]filter\./.test(window.location.search)) e.preventDefault();
    });
  }

  /* ---- 4. in-stock rides along with a size selection --------------------

     Ticking Madrid's own hidden `filter.v.availability` input rather than
     appending to the URL by hand: `<facet-filters-form>` builds its params from
     `new FormData(form)`, so the checkbox IS the mechanism, and the form, the
     URL and the re-render all stay in step. Appending to `location` would
     desync the form, which is the same reasoning the 7 Sep version used.

     Matched on the input NAME. `filter.v.availability` and
     `filter.v.option.size` are Shopify's own parameters; the GROUP LABELS above
     them are Search & Discovery settings anyone can rename in the admin. */
  function syncAvailability(container) {
    var wanted = !!container.querySelector("input[name='filter.v.option.size']:checked");

    /* THE GROUP HAS TWO CHECKBOXES, NOT ONE — `In stock` (value "1") and
       `Out of stock` (value "0") — and ticking both is not "availability on",
       it is "in stock OR out of stock", which matches everything. The first
       pass did exactly that and submitted
       `filter.v.availability=1&filter.v.availability=0`: the parameter was
       present, the URL looked right, and the result set was unchanged at 51
       products with Lennox still in it. Match on the VALUE, and untick the
       other one explicitly rather than leaving whatever was there. */
    Array.prototype.forEach.call(
      container.querySelectorAll("input[name='filter.v.availability']"),
      function (input) { input.checked = wanted && input.value === '1'; }
    );

    return wanted;
  }

  function buildFooter(container) {
    var footer = container.querySelector('.facets-remove__all');
    if (!footer) return;
    /* GUARD ON THE MARKUP, NOT A FLAG. Madrid re-renders by replacing inner
       html, so a `data-` flag on a surviving wrapper outlives the button it was
       meant to track — the same trap the sort menu hit. */
    if (footer.querySelector('.stl-facets__apply')) return;

    var form = container.closest('form') || document.querySelector('.facets__form');
    if (!form) return;

    var apply = document.createElement('button');
    apply.type = 'button';
    apply.className = 'stl-facets__apply';
    apply.textContent = 'Apply';
    apply.addEventListener('click', function () {
      /* Before the submit, so the checkbox is in the FormData this dispatch
         builds. Called on every Apply rather than only when a size is ticked, so
         that REMOVING the last size also removes the in-stock constraint — a
         one-way sync would strand it on for the rest of the session. */
      syncAvailability(container);
      busy();
      submit(form);
      /* Close the drawer the way Madrid does, rather than unsetting classes by
         hand — its close button owns the focus restore and the scroll lock. */
      var close = container.querySelector('.facets-modal__close');
      if (close) close.click();
    });

    footer.appendChild(apply);
    footer.classList.add('stl-facets__actions');
  }

  function init() {
    var container = document.querySelector('.facets__container');
    /* Apply still dispatches FROM the form, so its event never travels through
       the drawer and the guard above cannot see it. */
    guard(container);
    collapse();
    if (container) {
      buildFooter(container);
      wireClearAll(container);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var host = document.querySelector('.facets-container');
  if (host && window.MutationObserver) {
    new MutationObserver(function () { init(); }).observe(host, { childList: true, subtree: true });
  }
})();
